import { useRef, useState } from "react";

import { IconButton, TextField, Typography } from "@mui/material";
import classNames from "classnames";
import moment from "moment";
import { FaChevronLeft } from "react-icons/fa6";
import { IoSend } from "react-icons/io5";
import { Link, useParams } from "react-router-dom";

import { useAccount } from "../../../../../hooks/useAccount";
import { useChat } from "../../../../../hooks/useChat";
import { Loading } from "../../../../common/Loading/Loading";
import { RichTypography } from "../../../../common/RichTypography/RichTypography";
import noAvatarImage from "../../../../imagenes/no_avatar.svg";

export type TChatBoxProps = {
	loading: boolean;
};

export function ChatBox(props: TChatBoxProps) {
	const { account } = useAccount();
	const params = useParams();
	const userId = params.messagesUserId;
	const { chatMessages, splitMessageAndSend } = useChat();
	const chatMessage = chatMessages[userId ?? ""];
	const [text, setText] = useState("");
	const prevKeyCode = useRef("");

	if (!account) {
		return null;
	}

	const onClickSendMessage = () => {
		if (!text) {
			return;
		}

		const body = {
			body: text.trim(),
			receiverId: chatMessage?.participants.receiver.userId ?? "",
		};

		splitMessageAndSend(body);
		setText("");
	};

	const messages = chatMessage?.messages;
	const participants = chatMessage?.participants;

	return (
		<>
			<div className="chat-info">
				<Link to="/messages" replace>
					<IconButton>
						<FaChevronLeft className="normal-icon" />
					</IconButton>
				</Link>
				<img
					src={
						participants?.receiver.profile?.thumbnail ||
						participants?.receiver.profile?.url ||
						noAvatarImage
					}
					className="avatar"
				/>
				<Link
					to={`/profile/${participants?.receiver.profile?.profileSlug ?? ""}`}
					className="big-text bold opponent-name no-underline-link">
					{`${participants?.receiver.firstName ?? ""} ${participants?.receiver.lastName ?? ""}`.trim()}
				</Link>
			</div>
			<div className="messages-box">
				<div style={{ display: "flex", justifyContent: "center" }}>
					{props.loading && <Loading absolute={false} size={32} />}
				</div>
				{messages?.map((message) => (
					<div
						className={classNames("message-item", {
							"my-message": message.senderId === account.id,
						})}
						key={message.messageId}>
						<Link
							className="normal-text bold no-underline-link user-name"
							to={`/profile/${message.sender.profile?.profileSlug ?? ""}`}>
							{`${message.sender.firstName ?? ""} ${message.sender.lastName ?? ""}`.trim()}
						</Link>
						<Typography component="span" className="sent-time">
							{moment(message.createdAt).format("HH:mm, DD MMM, YYYY")}
						</Typography>
						<RichTypography
							whiteSpace="break-spaces"
							text={message.message}
							id={message.messageId}
							className="normal-text"
						/>
					</div>
				))}
				<div id="scroll-position" />
			</div>
			<div className="input-box">
				<TextField
					fullWidth
					variant="standard"
					multiline
					style={{ height: "calc(100% - 2px)", overflowY: "auto" }}
					placeholder="Type a message..."
					value={text}
					onChange={(event) => {
						setText(event.target.value);
					}}
					slotProps={{
						input: { disableUnderline: true, className: "normal-text" },
					}}
					onKeyDown={(event) => {
						if (
							event.code === "Enter" &&
							(prevKeyCode.current === "ShiftLeft" ||
								prevKeyCode.current === "ShiftRight")
						) {
							event.preventDefault();
							onClickSendMessage();
						}
						prevKeyCode.current = event.code;
					}}
				/>
				<IconButton
					type="submit"
					disabled={text.trim() === ""}
					onClick={onClickSendMessage}>
					<IoSend
						className={classNames("normal-icon send-icon", {
							disabled: text.trim() === "",
						})}
					/>
				</IconButton>
			</div>
		</>
	);
}
